/**
 * Centralizes the asset facts that drive visibility controls and table widths
 * so every asset view shares one presentation contract.
 */
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';

export type AssetViewFieldId =
  | 'title'
  | 'campaigns'
  | 'strategies'
  | 'assetType'
  | 'source'
  | 'releaseDate'
  | 'filename'
  | 'locale'
  | 'liveStatus'
  | 'schedule'
  | 'channel'
  | 'dimensions'
  | 'duration'
  | 'destinationUrl';

export interface AssetViewField {
  readonly id: AssetViewFieldId;
  readonly labelKey: string;
  readonly defaultVisible: boolean;
  readonly width: number;
  readonly alwaysVisible?: boolean;
  readonly featureFlag?: string;
}

export interface AssetViewPreferences {
  readonly fields: Readonly<Record<AssetViewFieldId, boolean>>;
}

export const ASSET_VIEW_FIELDS: readonly AssetViewField[] = [
  {
    id: 'title',
    labelKey: 'asset_detail_view_header_asset_title',
    defaultVisible: true,
    width: 280,
    alwaysVisible: true,
  },
  {
    id: 'campaigns',
    labelKey: 'asset_detail_view_header_campaigns',
    defaultVisible: true,
    width: 200,
    alwaysVisible: true,
  },
  {
    id: 'strategies',
    labelKey: 'asset_detail_view_header_strategies',
    defaultVisible: true,
    width: 200,
    alwaysVisible: true,
  },
  {
    id: 'assetType',
    labelKey: 'asset_detail_view_header_asset_type',
    defaultVisible: true,
    width: 100,
    alwaysVisible: true,
  },
  {
    id: 'source',
    labelKey: 'asset_detail_view_header_source',
    defaultVisible: true,
    width: 100,
    featureFlag: FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  },
  {
    id: 'releaseDate',
    labelKey: 'asset_detail_view_header_release_date',
    defaultVisible: true,
    width: 120,
    featureFlag: FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  },
  {
    id: 'filename',
    labelKey: 'asset_detail_view_header_filename',
    defaultVisible: true,
    width: 200,
  },
  {
    id: 'locale',
    labelKey: 'asset_detail_view_header_locale',
    defaultVisible: true,
    width: 100,
  },
  {
    id: 'liveStatus',
    labelKey: 'asset_view_field_liveStatus',
    defaultVisible: false,
    width: 110,
  },
  {
    id: 'schedule',
    labelKey: 'asset_view_field_schedule',
    defaultVisible: false,
    width: 200,
  },
  {
    id: 'channel',
    labelKey: 'asset_view_field_channel',
    defaultVisible: false,
    width: 140,
  },
  {
    id: 'dimensions',
    labelKey: 'asset_view_field_dimensions',
    defaultVisible: false,
    width: 110,
  },
  {
    id: 'duration',
    labelKey: 'asset_view_field_duration',
    defaultVisible: false,
    width: 90,
  },
  {
    id: 'destinationUrl',
    labelKey: 'asset_view_field_destinationUrl',
    defaultVisible: true,
    width: 150,
  },
];

export function getFieldWidth (id: AssetViewFieldId): number {
  return ASSET_VIEW_FIELDS.find((field) => field.id === id)?.width ?? 0;
}

function createDefaultFields (): Record<AssetViewFieldId, boolean> {
  const fields: Partial<Record<AssetViewFieldId, boolean>> = {};

  ASSET_VIEW_FIELDS.forEach((field) => {
    fields[field.id] = field.defaultVisible;
  });

  return fields as Record<AssetViewFieldId, boolean>;
}

const defaultFields = createDefaultFields();

export const DEFAULT_PREFERENCES: AssetViewPreferences = {
  fields: defaultFields,
};

export function isFieldVisible (
  preferences: AssetViewPreferences,
  id: AssetViewFieldId,
): boolean {
  const field = ASSET_VIEW_FIELDS.find((candidate) => candidate.id === id);

  if (field?.alwaysVisible === true) {
    return true;
  }

  if (
    field?.featureFlag !== undefined &&
    !isFeatureEnabled(field.featureFlag)
  ) {
    return false;
  }

  return preferences.fields[id] ?? field?.defaultVisible ?? false;
}
